/**
 * Binance Price Service - CORS-friendly alternative to CoinGecko
 * Uses Binance Public API which supports browser requests
 */

export interface BinancePriceData {
  usd: number;
  usd_24h_change: number;
  total_volume: number;
  market_cap: number;
}

export interface TokenPrices {
  [symbol: string]: BinancePriceData;
}

// Mapping of CoinGecko IDs to Binance trading pairs
const BINANCE_SYMBOL_MAP: { [key: string]: string } = {
  'tether': '1USDT', // USDT is the quote currency, so price is 1
  'usd-coin': 'USDCUSDT',
  'dai': 'USDTDAI', // DAIUSDT is 0, using USDTDAI (inverse)
  'wrapped-bitcoin': 'WBTCUSDT',
  'ethereum': 'ETHUSDT',
  'binancecoin': 'BNBUSDT',
  'chainlink': 'LINKUSDT',
  'staked-ether': 'ETHUSDT', // Use ETH as proxy for stETH
  'wrapped-steth': 'ETHUSDT', // Use ETH as proxy for wstETH
  'wrapped-beacon-eth': 'ETHUSDT', // Use ETH as proxy for wBETH
  'curve-dao-token': 'CRVUSDT',
  'binance-usd': 'BUSDUSDT',
  'euro-coin': 'EURUSDT', // Changed from EUROCUSDT to EURUSDT as proxy
  'paxos-standard': 'USDPUSDT', // USDPUSDT exists on Binance
  'ethena-usde': '1USDT', // USDe uses 1 as stable price
  'uniswap': 'UNIUSDT',
  'aave': 'AAVEUSDT',
  
  // Added mappings
  'alice': 'ALICEUSDT',
  'astr': 'ASTRUSDT',
  'bchsv': 'BCHSVUSDT',
  'dodo': 'DODOUSDT',
  'eos': 'EOSUSDT',
  'eosdown': 'EOSDOWNUSDT',
  'ethbull': 'ETHBULLUSDT',
  'fil': 'FILUSDT',
  'fxs': 'FXSUSDT',
  'ilv': 'ILVUSDT',
  'ksm': 'KSMUSDT',
  'mana': 'MANAUSDT',
  'mc': 'MCUSDT',
  'mkr': 'MKRUSDT',
  'near': 'NEARUSDT',
  'nu': 'NUUSDT',
  'oxt': 'OXTUSDT',
  'sushidown': 'SUSHIDOWNUSDT',
  'uniup': 'UNIUPUSDT',
  'yfii': 'YFIIUSDT',
  'zrx': 'ZRXUSDT',
  'avax': 'AVAXUSDT',
  'band': 'BANDUSDT',
  'bch': 'BCHUSDT',
  'btc': 'BTCUSDT',
  'btg': 'BTGUSDT',
  'chz': 'CHZUSDT',
  'ftm': 'FTMUSDT',
  'qtum': 'QTUMUSDT',
  'sand': 'SANDUSDT',
  'stx': 'STXUSDT',
  'uni': 'UNIUSDT',
  'waves': 'WAVESUSDT',
  'wbtc': 'WBTCUSDT',
  'weth': 'ETHUSDT',
  'tusd': 'TUSDUSDT',
  'link': 'LINKUSDT',
  
  // Fallbacks for missing tokens (using ETH or similar as proxy if needed, or just omit)
  'wsteth': 'ETHUSDT', // Proxy
};

interface Binance24hrTicker {
  symbol: string;
  priceChange: string;
  priceChangePercent: string;
  lastPrice: string;
  volume: string;
  quoteVolume: string;
}

/**
 * Fetch real-time prices from Binance Public API
 */
export const fetchBinancePrices = async (coingeckoIds: string[]): Promise<TokenPrices> => {
  const result: TokenPrices = {};
  
  try {
    // Get all 24hr ticker data from Binance
    const response = await fetch('https://api.binance.com/api/v3/ticker/24hr');
    
    if (!response.ok) {
      console.warn(`Binance API returned status: ${response.status}`);
      return {};
    }

    const allTickers: Binance24hrTicker[] = await response.json();
    
    // Create a map for quick lookup
    const tickerMap = new Map<string, Binance24hrTicker>();
    allTickers.forEach(ticker => {
      tickerMap.set(ticker.symbol, ticker);
    });

    // Process each requested token
    coingeckoIds.forEach(coingeckoId => {
      const binanceSymbol = BINANCE_SYMBOL_MAP[coingeckoId];
      
      if (binanceSymbol) {
        // Handle stablecoins with fixed price of 1
        if (binanceSymbol === '1USDT') {
          result[coingeckoId] = {
            usd: 1.0,
            usd_24h_change: 0.0,
            total_volume: 100000000, // Default volume
            market_cap: 0,
          };
        } else if (binanceSymbol === 'USDTDAI' && tickerMap.has(binanceSymbol)) {
          // Special handling for inverse pair USDT/DAI
          const ticker = tickerMap.get(binanceSymbol)!;
          const price = 1 / parseFloat(ticker.lastPrice);
          const change24h = -parseFloat(ticker.priceChangePercent); // Inverse change
          const volume = parseFloat(ticker.volume); // Volume in base currency (USDT) which is roughly USD
          
          result[coingeckoId] = {
            usd: price,
            usd_24h_change: change24h,
            total_volume: volume,
            market_cap: 0,
          };
        } else if (tickerMap.has(binanceSymbol)) {
          const ticker = tickerMap.get(binanceSymbol)!;
          const price = parseFloat(ticker.lastPrice);
          const change24h = parseFloat(ticker.priceChangePercent);
          const volume = parseFloat(ticker.quoteVolume); // Volume in quote currency (USDT)
          
          result[coingeckoId] = {
            usd: price,
            usd_24h_change: change24h,
            total_volume: volume,
            market_cap: 0, // Binance doesn't provide market cap, we'll need to calculate or use fallback
          };
        } else {
          console.warn(`Binance symbol ${binanceSymbol} not found in ticker data`);
        }
      } else {
        console.warn(`No Binance mapping for ${coingeckoId}`);
      }
    });

    console.log(`✅ Fetched prices for ${Object.keys(result).length} tokens from Binance`);
    return result;
    
  } catch (error) {
    console.error('Binance API error:', error);
    return {};
  }
};

/**
 * Fetch market cap data from CoinGecko (as fallback for market cap only)
 * This might fail due to CORS but we'll handle it gracefully
 */
export const fetchMarketCapFallback = async (coingeckoIds: string[]): Promise<{ [key: string]: number }> => {
  try {
    const ids = coingeckoIds.join(',');
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&per_page=100&page=1&sparkline=false`
    );

    if (!response.ok) {
      return {};
    }

    const data = await response.json();
    const marketCaps: { [key: string]: number } = {};
    
    data.forEach((coin: any) => {
      if (coin.market_cap) {
        marketCaps[coin.id] = coin.market_cap;
      }
    });

    return marketCaps;
  } catch (error) {
    // Silently fail - we'll use fallback values
    return {};
  }
};

/**
 * Fetch combined price data using Binance for prices/volume and fallback for market cap
 */
export const fetchTokenPrices = async (coingeckoIds: string[]): Promise<TokenPrices> => {
  console.log('🔄 Fetching prices from Binance API...');
  
  // Get prices and volume from Binance
  const binancePrices = await fetchBinancePrices(coingeckoIds);
  
  // Try to get market caps from CoinGecko (may fail silently)
  const marketCaps = await fetchMarketCapFallback(coingeckoIds);
  
  // Combine the data
  const combined: TokenPrices = {};
  
  Object.keys(binancePrices).forEach(id => {
    combined[id] = {
      ...binancePrices[id],
      market_cap: marketCaps[id] || 0, // Use CoinGecko market cap if available, otherwise 0
    };
  });
  
  console.log('📊 Combined price data:', combined);
  return combined;
};

/**
 * Estimate market cap based on circulating supply (if known)
 * This is a fallback when CoinGecko market cap is not available
 */
const ESTIMATED_SUPPLY: { [key: string]: number } = {
  'tether': 120000000000, // ~120B USDT
  'usd-coin': 35000000000, // ~35B USDC
  'dai': 5000000000, // ~5B DAI
  'wrapped-bitcoin': 150000, // ~150k WBTC
  'ethereum': 120000000, // ~120M ETH
  'binancecoin': 150000000, // ~150M BNB
  'chainlink': 600000000, // ~600M LINK
  'curve-dao-token': 1000000000, // ~1B CRV
  'binance-usd': 2000000000, // ~2B BUSD
  'paxos-standard': 150000000, // ~150M USDP estimate
};

export const estimateMarketCap = (coingeckoId: string, price: number): number => {
  const supply = ESTIMATED_SUPPLY[coingeckoId];
  if (supply) {
    return price * supply;
  }
  return 0;
};